/**
 * Pet-species chips (client change, 9 Oct 2026: "don't show Dog food when Bird is selected").
 *
 * Two flavours over the same option list:
 *  - `SpeciesFilterChips` — single choice with an "All pets" chip, for shopper filters.
 *  - `SpeciesMultiSelect` — any number, for a vendor tagging a product. None selected
 *    means "for all pets", which the API treats as matching every filter.
 *
 * Solid colour tokens only: slash-opacity classes (bg-primary/10) compile to nothing in this
 * console because the theme colours have no alpha slot.
 */
import { cn } from '@/lib/utils';
import { SPECIES_OPTIONS, type PetSpecies } from '@/features/pet/types';

const chip = (active: boolean) =>
  cn(
    'rounded-full border-2 px-3 py-1 text-xs font-semibold transition-colors',
    active
      ? 'border-primary bg-primary text-primary-foreground'
      : 'border-border bg-card text-muted-foreground hover:border-primary hover:text-foreground',
  );

export function SpeciesFilterChips({
  value,
  onChange,
  testid = 'species-filter',
}: {
  value: PetSpecies | '';
  onChange: (next: PetSpecies | '') => void;
  testid?: string;
}) {
  const options: { value: PetSpecies | ''; label: string }[] = [{ value: '', label: 'All pets' }, ...SPECIES_OPTIONS];
  return (
    <div className="flex flex-wrap gap-1.5" data-testid={testid} role="group" aria-label="Filter by pet">
      {options.map((o) => (
        <button
          key={o.value || 'ALL'}
          type="button"
          onClick={() => onChange(o.value)}
          data-testid={`${testid}-${o.value || 'ALL'}`}
          data-active={value === o.value}
          aria-pressed={value === o.value}
          className={chip(value === o.value)}
        >
          {o.label}
        </button>
      ))}
    </div>
  );
}

export function SpeciesMultiSelect({
  value,
  onChange,
  testid = 'species-select',
}: {
  value: PetSpecies[];
  onChange: (next: PetSpecies[]) => void;
  testid?: string;
}) {
  const toggle = (s: PetSpecies) => onChange(value.includes(s) ? value.filter((x) => x !== s) : [...value, s]);
  return (
    <div className="flex flex-col gap-1.5">
      <div className="flex flex-wrap gap-1.5" data-testid={testid} role="group" aria-label="Suitable for">
        {SPECIES_OPTIONS.map((o) => (
          <button
            key={o.value}
            type="button"
            onClick={() => toggle(o.value)}
            data-testid={`${testid}-${o.value}`}
            data-active={value.includes(o.value)}
            aria-pressed={value.includes(o.value)}
            className={chip(value.includes(o.value))}
          >
            {o.label}
          </button>
        ))}
      </div>
      <p className="text-xs text-muted-foreground">
        {value.length === 0
          ? 'None selected — shown to shoppers with any pet.'
          : 'Only shown when shoppers filter for these pets (or no pet).'}
      </p>
    </div>
  );
}
